import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import { formatAddress } from 'dashboard/lib/utils/formatters';
import {
  CaliforniaAttorneyGeneralStatus,
  ReportBusinessNonprofit,
} from 'dashboard/models/report/business-nonprofit';
import { ReportSnapshotBusinessNonprofit } from 'dashboard/models/report-snapshot';
import { AttributeGrid } from 'design-system/components/Attribute';
import Attribute from 'design-system/components/Attribute/Attribute';
import Card from 'design-system/components/Card';
import { TitleContainer } from 'design-system/components/Card/Header';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface BusinessNonprofitProps {
  report: ReportBusinessNonprofit | ReportSnapshotBusinessNonprofit;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledSticker = styled(Sticker)`
  margin-bottom: ${Spacing[1]};
`;

const CaliforniaAGStatusSticker = ({
  status,
}: {
  status: CaliforniaAttorneyGeneralStatus | undefined;
}) => {
  if (status == null) return <></>;

  switch (status) {
    case CaliforniaAttorneyGeneralStatus.MayOperate:
      return <StyledSticker variant='green'>{status}</StyledSticker>;
    case CaliforniaAttorneyGeneralStatus.MayNotOperate:
    case CaliforniaAttorneyGeneralStatus.NotOperating:
      return <StyledSticker variant='danger'>{status}</StyledSticker>;
    case CaliforniaAttorneyGeneralStatus.Undetermined:
      return <StyledSticker variant='basic'>{status}</StyledSticker>;
    default:
      assertNever(status);
  }
};

// Descriptions come from https://developer.candid.org/docs/interpret-charity-check-api-responses#filing-requirement-code
const irsFilingRequirementCodeDescription: Record<string, string> = {
  '000': 'This organization is not required to file an annual return with the IRS.',
  '001': 'This organization is required to file an IRS Form 990-PF.',
  '010': 'This organization is required to file an IRS Form 990 or 990-EZ.',
  '011': 'This organization is required to file an IRS Form 990-PF.',
  '020': 'This organization is required to file an IRS Form 990-N.',
  '021': 'This organization is required to file an IRS Form 990-N.',
  '030': 'This organization is required to file a group return with the IRS.',
  '031': 'This organization is required to file an IRS Form 990-PF group return.',
  '040': 'This organization is required to file an IRS Form 990-BL, return for Black Lung Trusts.',
  '060':
    'This organization is not required to file an annual return with the IRS because it is a church.',
  '061':
    'This organization is not required to file an annual return with the IRS because it is a church.',
  '070':
    'This organization is not required to file an annual return with the IRS because it is a government 501 c (1).',
  '130':
    'This organization is not required to file an annual return with the IRS because it is a religious organization.',
  '140':
    'This organization is not required to file an annual return with the IRS because it is an arm of a state or local government.',
  '141':
    'This organization is not required to file an annual return with the IRS because it is an arm of a state or local government.',
};

const extractDate = (dateString: string | undefined) => {
  if (dateString) return dateString.match(/\d{1,2}\/\d{1,2}\/\d{4}/);
};

const BusinessNonprofit = ({ report, isRedacted, multiCardFormat }: BusinessNonprofitProps) => {
  const isSnapshot = report.type === ApiModel.ReportSnapshotBusinessNonprofit;

  const { 'has-match': hasMatch, result: result } = report.attributes;

  const {
    'organization-name': organizationName,
    'organization-name-aka': organizationNameAka,
    address: address,
    'foundation-type-code': foundationTypeCode,
    'foundation-type-description': foundationTypeDescription,
    'irs-pub-78-details': irsPub78Details,
    'irs-bmf-details': irsBmfDetails,
    'ca-ag-details': caAgDetails,
    'ca-ftb-details': caFtbDetails,
    'irs-filing-requirement-code': irsFilingRequirementCode,
  } = result ?? {};

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {!isRedacted && (
        <TitleContainer>
          {hasMatch ? (
            <div>
              <Card.Title>{organizationName || 'Name not found'}</Card.Title>
              {organizationNameAka && (
                <Card.Subtitle>{`AKA: ${organizationNameAka}`}</Card.Subtitle>
              )}
              <AttributeGrid colCount={3}>
                <Attribute
                  label='Address'
                  value={formatAddress({
                    addressStreet1: address?.['street-1'],
                    addressStreet2: address?.['street-2'],
                    addressCity: address?.city,
                    addressSubdivision: address?.subdivision,
                    addressPostalCode: address?.['postal-code'],
                    color: 'primary',
                  })}
                  placeholder='-'
                  redacted={isRedacted}
                />
                <Attribute
                  label='Foundation Type Code'
                  value={foundationTypeCode}
                  placeholder='-'
                  redacted={isRedacted}
                />
                <Attribute
                  label='Foundation Type Description'
                  value={foundationTypeDescription}
                  placeholder='-'
                  redacted={isRedacted}
                />
                <Attribute
                  label='IRS Filing Requirement Code'
                  value={irsFilingRequirementCode}
                  placeholder='-'
                  redacted={isRedacted}
                />
                <Attribute
                  label='IRS Filing Requirement Code Description'
                  value={
                    irsFilingRequirementCode != null &&
                    irsFilingRequirementCodeDescription[irsFilingRequirementCode]
                  }
                  placeholder='-'
                  redacted={isRedacted}
                />
              </AttributeGrid>
            </div>
          ) : (
            <Card.Title>This organization was not found in any of the data sources.</Card.Title>
          )}
        </TitleContainer>
      )}
      {irsPub78Details && (
        <section>
          <HeaderContainer>
            <Paragraph color='primary' size='lg' weight='bold'>
              IRS Publication 78 Details
            </Paragraph>
            {irsPub78Details.verified ? (
              <StyledSticker variant='green'>Verified</StyledSticker>
            ) : (
              <StyledSticker variant='danger'>Unverified</StyledSticker>
            )}
          </HeaderContainer>
          <AttributeGrid colCount={3}>
            <Attribute
              label='Organization Name'
              value={irsPub78Details['organization-name']}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Location'
              value={[irsPub78Details.city, irsPub78Details.state].filter((x) => x).join(', ')}
              placeholder='-'
              redacted={isRedacted}
            />
            {!isSnapshot && (
              <Attribute
                label='Most Recent IRS Publication 78'
                value={extractDate(irsPub78Details['most-recent-pub78'])}
                placeholder='-'
                redacted={isRedacted}
              />
            )}

            <Attribute
              label='EIN'
              value={irsPub78Details.ein}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Deductibility Status Description'
              value={irsPub78Details['organization-types']?.[0]?.['organization-type']}
              placeholder='-'
              redacted={isRedacted}
            />
            {!isSnapshot && (
              <Attribute
                label='Most Recent Internal Revenue Bulletin'
                value={extractDate(irsPub78Details['most-recent-irb'])}
                placeholder='-'
                redacted={isRedacted}
              />
            )}
          </AttributeGrid>
        </section>
      )}
      {irsBmfDetails && (
        <section>
          <HeaderContainer>
            <Paragraph color='primary' size='lg' weight='bold'>
              IRS Business Master File Details
            </Paragraph>
            {irsBmfDetails.status ? (
              <StyledSticker variant='green'>Present on BMF</StyledSticker>
            ) : (
              <StyledSticker variant='danger'>Not Present on BMF</StyledSticker>
            )}
          </HeaderContainer>
          <AttributeGrid colCount={3}>
            <Attribute
              label='Organization Name'
              value={irsBmfDetails['organization-name']}
              placeholder='-'
              redacted={isRedacted}
            />
            {!isSnapshot && (
              <Attribute
                label='Most Recent IRS BMF'
                value={extractDate(irsBmfDetails['most-recent-bmf'])}
                placeholder='-'
                redacted={isRedacted}
              />
            )}
            <Attribute
              label='EIN'
              value={irsBmfDetails.ein}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='IRS Subsection'
              value={irsBmfDetails['subsection-description']}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Ruling Date'
              value={irsBmfDetails['ruling-month'] + '/' + irsBmfDetails['ruling-year']}
              placeholder='-'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </section>
      )}
      {caAgDetails && (
        <section>
          <HeaderContainer>
            <Paragraph color='primary' size='lg' weight='bold'>
              California Attorney General Details
            </Paragraph>
            <CaliforniaAGStatusSticker status={caAgDetails.status} />
          </HeaderContainer>
          <AttributeGrid>
            <Attribute
              label='Registry Status'
              value={caAgDetails['registry-status']}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='State Charity Registration Number'
              value={caAgDetails['state-charity-reg-num']}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Issue Date'
              value={extractDate(caAgDetails['issue-date'])}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Last Renewal Date'
              value={extractDate(caAgDetails['last-renewal-date'])}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Status Date'
              value={extractDate(caAgDetails['status-set-date'])}
              placeholder='-'
              redacted={isRedacted}
            />
            <Attribute
              label='Status as of Date'
              value={extractDate(caAgDetails['as-of-date'])}
              placeholder='-'
              redacted={isRedacted}
            />
            {!isSnapshot && (
              <Attribute
                label='Most Recent Attorney General Date'
                value={extractDate(caAgDetails['most-recent-ag'])}
                placeholder='-'
                redacted={isRedacted}
              />
            )}
          </AttributeGrid>
        </section>
      )}
      {caFtbDetails && (
        <section>
          <HeaderContainer>
            <Paragraph color='primary' size='lg' weight='bold'>
              California Franchise Tax Board Revoked List Details
            </Paragraph>
            {caFtbDetails['revocation-status'] ? (
              <StyledSticker variant='danger'>Present on Revoked List</StyledSticker>
            ) : (
              <StyledSticker variant='green'>Not Present on Revoked List</StyledSticker>
            )}
          </HeaderContainer>
          {caFtbDetails['revocation-status'] ? (
            <AttributeGrid>
              <Attribute
                label='Status as of Date'
                value={extractDate(caFtbDetails['revocation-status-date'])}
                placeholder='-'
                redacted={isRedacted}
              />
              {!isSnapshot && (
                <Attribute
                  label='Most Recent FTB Date'
                  value={extractDate(caFtbDetails['most-recent-ftb'])}
                  placeholder='-'
                  redacted={isRedacted}
                />
              )}
            </AttributeGrid>
          ) : (
            <Paragraph>
              This organization is not on the FTB Revoked Exempt Organizations List
            </Paragraph>
          )}
        </section>
      )}
    </ReportResultLayout>
  );
};

export default BusinessNonprofit;
